import { fixedUi } from '@/i18n/fixedUi';
import {
  attributeDisplayLabel,
  attributeValueDisplayLabel,
} from '@/shein-platform/utils/sheinAttributeTemplate';

const EMPTY = '（空）';

function normalizeForCompare(value) {
  if (value == null) return null;
  if (Array.isArray(value)) {
    return value.map((row) => normalizeForCompare(row));
  }
  if (typeof value === 'object') {
    const out = {};
    Object.keys(value).sort().forEach((key) => {
      out[key] = normalizeForCompare(value[key]);
    });
    return out;
  }
  return value;
}

function valuesEqual(a, b) {
  return JSON.stringify(normalizeForCompare(a)) === JSON.stringify(normalizeForCompare(b));
}

function resolveAttrDef(attrDefs = [], attributeId) {
  const id = String(attributeId || '');
  return (Array.isArray(attrDefs) ? attrDefs : []).find(
    (attr) => String(attr?.attributeId || '') === id,
  ) || null;
}

function lookupValueName(attrDef, valueId) {
  const id = String(valueId ?? '').trim();
  if (!id || !attrDef) return id;
  const values = Array.isArray(attrDef.values) ? attrDef.values : [];
  const hit = values.find((v) => String(v?.id ?? v?.attributeValueId ?? '') === id);
  if (!hit) return id;
  return attributeValueDisplayLabel(hit) || id;
}

/** 把属性值拆成可读片段（用于标签式展示） */
export function formatProductAttributeDisplayParts(value, attrDef = null) {
  if (value == null || value === '') return [EMPTY];
  if (Array.isArray(value)) {
    if (!value.length) return [EMPTY];
    const parts = value.map((row) => {
      if (row == null || row === '') return '';
      if (typeof row === 'object') {
        const valueId = row.valueId ?? row.attributeValueId ?? row.id;
        const name = lookupValueName(attrDef, valueId)
          || attributeValueDisplayLabel(row)
          || String(valueId || '').trim();
        const percent = row.percent ?? row.attributeExtraValue;
        if (percent != null && String(percent).trim() !== '') {
          return `${name || '成分'} ${String(percent).trim()}%`;
        }
        return name || '';
      }
      return lookupValueName(attrDef, row) || String(row);
    }).filter(Boolean);
    return parts.length ? parts : [EMPTY];
  }
  if (typeof value === 'object') {
    const valueId = value.valueId ?? value.attributeValueId ?? value.id;
    const name = lookupValueName(attrDef, valueId)
      || attributeValueDisplayLabel(value)
      || String(valueId || '').trim();
    const percent = value.percent ?? value.attributeExtraValue;
    if (percent != null && String(percent).trim() !== '') {
      return [`${name || '值'} ${String(percent).trim()}%`];
    }
    if (name) return [name];
    return [EMPTY];
  }
  const text = String(value).trim();
  if (!text) return [EMPTY];
  return [lookupValueName(attrDef, text) || text];
}

/** 把属性值格式化成可读文案 */
export function formatProductAttributeDisplayValue(value, attrDef = null) {
  const parts = formatProductAttributeDisplayParts(value, attrDef);
  if (!parts.length) return EMPTY;
  if (parts.length === 1 && parts[0] === EMPTY) return EMPTY;
  return parts.join('、');
}

/**
 * 对比当前表单类目属性 vs 最新属性库内容。
 * @returns {{ changes: Array<{id,label,before,after,afterParts}>, count: number }}
 */
export function diffProductAttributeChanges({
  current = {},
  next = {},
  attrDefs = [],
} = {}) {
  const currentMap = current && typeof current === 'object' ? current : {};
  const nextMap = next && typeof next === 'object' ? next : {};
  const ids = new Set([
    ...Object.keys(currentMap),
    ...Object.keys(nextMap),
  ]);
  const changes = [];
  ids.forEach((id) => {
    const beforeRaw = currentMap[id];
    const afterRaw = nextMap[id];
    if (valuesEqual(beforeRaw, afterRaw)) return;
    const attrDef = resolveAttrDef(attrDefs, id);
    const afterParts = formatProductAttributeDisplayParts(afterRaw, attrDef);
    changes.push({
      id: String(id),
      label: attributeDisplayLabel(attrDef) || `属性 ${id}`,
      before: formatProductAttributeDisplayValue(beforeRaw, attrDef),
      after: afterParts.join('、'),
      afterParts,
    });
  });
  changes.sort((a, b) => String(a.label).localeCompare(String(b.label), 'zh'));
  return { changes, count: changes.length };
}

/** 将属性差异列表转成 attributeId → change 映射，供字段下方提示使用 */
export function indexAttributeChangeHints(preview, active = true) {
  if (!active) return {};
  const rows = preview?.changes;
  if (!Array.isArray(rows) || !rows.length) return {};
  const map = {};
  rows.forEach((row) => {
    if (row?.id == null) return;
    map[String(row.id)] = row;
  });
  return map;
}

function flattenSizeChart(sizeChart = {}) {
  const out = {};
  Object.entries(sizeChart || {}).forEach(([rowId, cell]) => {
    if (!cell || typeof cell !== 'object') {
      out[String(rowId)] = cell;
      return;
    }
    Object.entries(cell).forEach(([attrId, val]) => {
      out[`${rowId}::${attrId}`] = val;
    });
  });
  return out;
}

function sizeChartCellLabel(key, measureAttrs = []) {
  const [rowId, attrId] = String(key).split('::');
  const attr = (Array.isArray(measureAttrs) ? measureAttrs : []).find(
    (item) => String(item?.attributeId || '') === String(attrId || ''),
  );
  const attrName = attributeDisplayLabel(attr) || attrId || fixedUi("尺寸");
  if (!rowId || rowId === 'base' || rowId === 'BASE') {
    return attrName;
  }
  return `${attrName}（尺码 ${rowId}）`;
}

/**
 * 对比当前尺码表 vs 最新尺码库。
 */
export function diffSizeChartChanges({
  current = {},
  next = {},
  measureAttrs = [],
} = {}) {
  const a = flattenSizeChart(current);
  const b = flattenSizeChart(next);
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  const changes = [];
  keys.forEach((key) => {
    const beforeRaw = a[key];
    const afterRaw = b[key];
    if (valuesEqual(beforeRaw, afterRaw)) return;
    const before = beforeRaw == null || String(beforeRaw).trim() === ''
      ? EMPTY
      : String(beforeRaw).trim();
    const after = afterRaw == null || String(afterRaw).trim() === ''
      ? EMPTY
      : String(afterRaw).trim();
    changes.push({
      id: key,
      label: sizeChartCellLabel(key, measureAttrs),
      before,
      after,
    });
  });
  changes.sort((aItem, bItem) => String(aItem.label).localeCompare(String(bItem.label), 'zh'));
  return { changes, count: changes.length };
}

export function buildTemplateChangeConfirmMessage(changes = [], {
  intro = '以下内容与模板库最新版本不一致，重新加载将用最新模板覆盖对应区域：',
  emptyText = '未解析到字段级差异（模板元数据或结构可能已变）。重新加载仍会用最新模板覆盖该区域。',
} = {}) {
  if (!Array.isArray(changes) || changes.length === 0) {
    return emptyText;
  }
  const lines = changes.slice(0, 30).map((row, index) => (
    `${index + 1}. ${row.label}\n   当前：${row.before}\n   最新：${row.after}`
  ));
  const more = changes.length > 30 ? `\n…其余 ${changes.length - 30} 项` : '';
  return `${intro}\n\n${lines.join('\n\n')}${more}`;
}
